import { useMemo, useState } from 'react';
import { OverlaySpinner } from '@ankr.com/ui';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { useChainData } from 'modules/common';
import { useTranslation } from 'modules/i18n';
import { Layout } from 'modules/layout';
import { useAppSelector, useGetAccountAddressQuery } from 'modules/store';

import { GetTasksResult, useGetTasksQuery } from '../actions/getTasks';
import {
  ANY_NETWORK_VALUE,
  Filters,
  FilterValues,
  StatusFilter,
} from '../components/Filters/Filters';
import { GroupedTasksTable } from '../components/GroupedTasksTable/GroupedTasksTable';
import { TasksTable } from '../components/TasksTable/TasksTable';
import translation from './dashboard.translation';

const DEFAULT_FILTER_STATE: FilterValues = {
  status: StatusFilter.Any,
  networkValue: ANY_NETWORK_VALUE,
  search: '',
};

const EMPTY_TASKS: GetTasksResult = [];

export function Dashboard(): JSX.Element {
  const { classes } = useStyles();
  const { t, keys } = useTranslation(translation);
  const [filterValues, setFilterValues] =
    useState<FilterValues>(DEFAULT_FILTER_STATE);
  const { groupedTasks } = useAppSelector(state => state.ui);

  const chainData = useChainData();
  const { data: accountAddress = '' } = useGetAccountAddressQuery();
  const { data: tasks = EMPTY_TASKS, isLoading } = useGetTasksQuery({
    accountAddress,
    chainId: chainData?.chainId || null,
  });

  const filteredTasks = useMemo<GetTasksResult>(
    () =>
      tasks
        .filter(task => {
          if (!filterValues.search) {
            return true;
          }

          const isName = task.name
            .toLowerCase()
            .startsWith(filterValues.search.toLowerCase());

          const isAddress = Boolean(
            task.cronTarget
              ?.toLowerCase()
              .startsWith(filterValues.search.toLowerCase()),
          );

          return isName || isAddress;
        })
        .filter(task => {
          switch (filterValues.status) {
            case StatusFilter.Active:
              return !task.isPaused;
            case StatusFilter.Paused:
              return task.isPaused;
            case StatusFilter.Any:
            default:
              return true;
          }
        })
        .filter(
          task =>
            filterValues.networkValue === ANY_NETWORK_VALUE ||
            filterValues.networkValue === task.chainId,
        ),
    [filterValues, tasks],
  );

  const content = groupedTasks ? (
    <GroupedTasksTable tasks={filteredTasks} />
  ) : (
    <TasksTable tasks={filteredTasks} />
  );

  return (
    <Layout>
      <div className={classes.container}>
        <Typography variant="h6" component="h2" className={classes.title}>
          {t(keys.title)}
        </Typography>

        <div className={classes.header}>
          <Filters
            networkValue={filterValues.networkValue}
            status={filterValues.status}
            search={filterValues.search}
            onChange={filters =>
              setFilterValues(oldFilters => ({
                ...oldFilters,
                ...filters,
              }))
            }
          />
        </div>
        {isLoading ? <OverlaySpinner sx={{ flex: 1 }} /> : content}
      </div>
    </Layout>
  );
}

const useStyles = makeStyles()(theme => ({
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(20, 0),

    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4, 0),
    },
  },
  header: {
    marginBottom: theme.spacing(5),

    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(4),
    },
  },
  title: {
    '&&': {
      marginBottom: theme.spacing(10),

      [theme.breakpoints.down('md')]: {
        marginBottom: theme.spacing(8),
      },
    },
  },
}));
